import React, { createContext, forwardRef, useContext } from 'react';
import type { ComponentType } from 'react';

import { getLogger } from '@confluence/logger';

import { addLoadableId, preloadIfNeeded } from './preloader.ts';

const logger = getLogger('trackReactLazy');

type LazyModule = { default: ComponentType<any> };

export type TrackReactLazyOptions = {
	/** The loader reads values from the function that calls `lazy()`, so it cannot run before render. */
	renderTime?: boolean;
	/** `file:line` of a render-time loader, for the development warning. */
	source?: string;
};

// Marks a subtree whose code only loads when a render-time lazy component renders. The client
// cannot load it before hydration, so lazy components inside it must not hold hydration.
const RenderTimeLazyAncestor = createContext(false);

const warnedRenderTimeIds = new Set<string>();

// React 18's lazy initializer checks the component's status as soon as `then` returns, and suspends
// unless `then` has already called back. `Promise.resolve(loaded)` always calls back in a
// microtask, so it would suspend even for a loaded module. This Promise overrides `then` to call
// back synchronously. The rest of the Promise API goes through `then`, so `catch`, `finally` and
// `Promise.all` still work.
const resolvedThenable = <T,>(value: T): Promise<T> => {
	const promise = new Promise<T>((resolve) => resolve(value));
	promise.then = ((onFulfilled?: (value: T) => unknown) =>
		Promise.resolve(onFulfilled ? onFulfilled(value) : value)) as typeof promise.then;
	return promise;
};

function withServerTracking<M extends LazyModule>(
	id: string,
	module: M,
	{ renderTime, source }: TrackReactLazyOptions,
): M {
	const Original = module.default;

	const TrackedLazy = forwardRef<unknown, Record<string, unknown>>((props, ref) => {
		const hasRenderTimeAncestor = useContext(RenderTimeLazyAncestor);
		const element = <Original {...props} ref={ref} />;

		if (renderTime) {
			if (process.env.NODE_ENV !== 'production' && !warnedRenderTimeIds.has(id)) {
				warnedRenderTimeIds.add(id);
				logger.warn`React.lazy at ${source} rendered during SSR with a loader that reads render-time values, so its code split cannot be preloaded before hydration. Move the lazy() call to module scope.`;
			}
			return <RenderTimeLazyAncestor.Provider value>{element}</RenderTimeLazyAncestor.Provider>;
		}

		// Effects do not run during SSR, so render is the only place to record the rendered split.
		if (!hasRenderTimeAncestor) {
			addLoadableId(id);
		}
		return element;
	});
	TrackedLazy.displayName = `TrackedLazy(${Original.displayName || Original.name || 'Component'})`;

	return { ...module, default: TrackedLazy };
}

/**
 * Wraps a `React.lazy` loader so SSR can report the code split it rendered and the client can load
 * that split before hydration. LoadableReactLazyBabelPlugin injects the calls; do not call it
 * directly.
 */
export function trackReactLazy<M extends LazyModule>(
	id: string,
	loader: () => Promise<M>,
	options: TrackReactLazyOptions = {},
): () => Promise<M> {
	// process.env.REACT_SSR is read inline so each build can drop the other side's branch. None of
	// these branches changes rendered markup, so they cannot cause a hydration mismatch.
	const prepare = (module: M): M =>
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		process.env.REACT_SSR ? withServerTracking(id, module, options) : module;

	if (options.renderTime) {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		return process.env.REACT_SSR ? () => loader().then(prepare) : loader;
	}

	let loaded: M | undefined;
	let pending: Promise<M> | undefined;
	const load = (): Promise<M> => {
		pending ??= loader().then(
			(module) => {
				loaded = prepare(module);
				return loaded;
			},
			(error) => {
				pending = undefined;
				throw error;
			},
		);
		return pending;
	};

	let preloaded = false;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR) {
		// preloadIfNeeded can start loading at once. Waiting a microtask lets the defining module
		// finish evaluating before the loader reads any of its bindings.
		preloadIfNeeded(id, undefined, () =>
			Promise.resolve()
				.then(load)
				.then(() => {
					preloaded = true;
				}),
		);
	}

	return () => {
		if (!loaded) {
			return load();
		}
		// The server always answers synchronously once the module is loaded. The client only does so
		// after the preload the server asked for.
		const canResolveSynchronously =
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			process.env.REACT_SSR ? true : preloaded;
		return canResolveSynchronously ? resolvedThenable(loaded) : load();
	};
}
